'use client';

import { SearchIcon } from '@repo/icons';
import { useBreakpoint } from '@repo/react-hooks';
import { CurrencyIcon } from '@repo/ui/currency-icon';
import { Input } from '@repo/ui/input';
import { NoData } from '@repo/ui/no-data';
import { filterInData } from '@repo/utils/filter-in-data';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';
import { type ViewType } from '@/app/[locale]/(account)/account/withdraw';
import Method from '@/components/common/method-item';
import PaymentMethodsListSkeleton from '@/components/common/skeletons/payment-methods-list-skeleton';
import { PAYMENT_TYPES } from '@/constants';
import { type FullMethod, usePaymentMethodsWithNetworks } from '@/hooks/usePaymentMethodsWithNetworks';
import { getActiveMethods } from '@/hooks/usePaymentMethodsWithNetworks/utils';
import { useTenant } from '@/providers/TenantProvider';
import { type PaymentTypes } from '@/server-actions/payment-methods';

type PaymentMethodsListProps = {
  paymentType: PaymentTypes;
  view: ViewType;
  setView: (view: ViewType) => void;
  selectedMethod?: FullMethod;
  setSelectedMethod: (method?: FullMethod) => void;
  isQuickDeposit?: boolean;
};

const confPaymentType = {
  [PAYMENT_TYPES.DEPOSITS]: 'deposit',
  [PAYMENT_TYPES.WITHDRAWALS]: 'withdraw',
};

const PaymentMethodsList = ({
  setView,
  view,
  paymentType,
  selectedMethod,
  setSelectedMethod,
  isQuickDeposit,
}: PaymentMethodsListProps) => {
  const t = useTranslations(paymentType);
  const { matches: isMobile } = useBreakpoint('sm', 'max');

  const {
    tenantConfig: { paymentMethods },
  } = useTenant();

  const { isLoading, topMethods, otherMethods, creditMethods } = usePaymentMethodsWithNetworks({ paymentType });

  const allMethods = useMemo(() => [...topMethods, ...otherMethods], [topMethods, otherMethods]);

  const configKey = confPaymentType[paymentType as keyof typeof confPaymentType] as 'deposit' | 'withdraw';
  const currentMethods = paymentMethods?.[configKey] ?? [];

  const { hasCryptoMoonpay, hasFiat } = getActiveMethods(currentMethods) || {};

  const [searchValue, setSearchValue] = useState('');

  const filtered = useMemo(() => {
    return filterInData(allMethods, ['currencyCode'], searchValue) as FullMethod[];
  }, [allMethods, searchValue]);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    if ((!selectedMethod && topMethods.length > 0) || !!topMethods[0]?.networkOptions?.length) {
      setView('CRYPTO_DETAILS');
      setSelectedMethod(topMethods[0]);
    }
  }, [isMobile, setSelectedMethod, topMethods?.[0]?.networkOptions?.length]);

  if (isLoading) {
    return (
      <PaymentMethodsListSkeleton
        hasCryptoMoonpay={hasCryptoMoonpay && !isQuickDeposit}
        hasFiat={hasFiat && !isQuickDeposit}
      />
    );
  }

  const showList = searchValue ? filtered : topMethods;

  return (
    <section className="flex h-full w-full flex-col justify-between">
      <div className="h-full space-y-2.5 rounded-t-md p-2 vt-containers-settings-box-normal-bg sm:rounded-md">
        <Input
          placeholder={t('search-payment-method-placeholder')}
          value={searchValue}
          icon={<SearchIcon />}
          onChange={({ target: { value } }) => setSearchValue(value)}
        />

        {searchValue && filtered.length === 0 ? (
          <NoData
            title={t('no-results')}
            className="p-3 vt-containers-settings-box-light-bg"
          />
        ) : (
          <>
            {showList.map((item) => (
              <Method
                key={`${item.providerId}-${item.currencyCode}`}
                title={item.currencyCode}
                subTitle={item?.networkOptions?.map((n) => n.label).join(', ') || item.currencyCode}
                onClick={() => {
                  setView('CRYPTO_DETAILS');
                  setSelectedMethod(item);
                }}
                isActive={item.currencyCode === selectedMethod?.currencyCode}
                icons={[
                  <CurrencyIcon
                    key={`${item.providerId}-${item.currencyCode}`}
                    currency={item?.currencyCode}
                  />,
                ]}
              />
            ))}

            {!searchValue && otherMethods.length > 0 && (
              <Method
                title={t('other-coins-item-label')}
                iconPosition="end"
                isActive={
                  view === 'OTHER_COINS' ||
                  !!otherMethods.find((method) => method.currencyCode === selectedMethod?.currencyCode)
                }
                icons={otherMethods.slice(0, 3).map((item, i) => (
                  <CurrencyIcon
                    key={`${item.currencyCode}-other-icon-${i}`}
                    currency={item?.currencyCode?.toLowerCase()}
                  />
                ))}
                onClick={() => {
                  setView('OTHER_COINS');
                  setSelectedMethod(selectedMethod);
                }}
              />
            )}
          </>
        )}
      </div>

      {!isQuickDeposit && (hasFiat || hasCryptoMoonpay) && (
        <>
          <div className="flex w-full items-center justify-center vt-containers-settings-box-light-bg sm:bg-transparent sm:py-2">
            <span className="my-0.5 h-0.5 w-[95%] rounded-full bg-[var(--colors-dark-100)] sm:hidden" />
          </div>
          <div className="space-y-2.5 rounded-b-md p-2 vt-containers-settings-box-light-bg sm:rounded-md">
            {hasFiat && (
              <Method
                title={t('fiat-item-label')}
                subTitle={t('bank-gateway')}
                isActive={view === 'FIAT'}
                onClick={() => {
                  setView('FIAT');
                  // TODO CHANGE: currencyCode should be dynamic, for now we have only IRT fiat
                  setSelectedMethod(creditMethods?.[0] as FullMethod);
                }}
              />
            )}

            {hasCryptoMoonpay && (
              <Method
                title={t('credit-card-item-label')}
                subTitle="Visa, MC, PayPal, Google Pay, Apple Pay"
                isActive={view === 'CREDIT_CARDS'}
                onClick={() => {
                  setView('CREDIT_CARDS');
                  setSelectedMethod({} as FullMethod);
                }}
              />
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default PaymentMethodsList;
